import React from "react";
import { Box, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import AcxChip from "components/UI/AcxChip";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import theme from "Theme/AppTheme";
import {
    formatBusinessDays,
    formatDate,
    formatDateTime,
    formatHours,
    formatNumber,
} from "./usageValueFormat";

const TOTAL_ROW_ID = "org-total";

const TwoLineCell = ({
    primary,
    secondary,
    bold,
    alignRight,
}: {
    primary: React.ReactNode;
    secondary?: React.ReactNode;
    bold?: boolean;
    alignRight?: boolean;
}) => (
    <Box
        sx={{
            py: 1,
            px: 2,
            minWidth: 0,
            width: "100%",
            textAlign: alignRight ? "right" : "left",
        }}
    >
        <Typography
            sx={{
                fontSize: "13px",
                fontFamily: bold ? "Inter Bold" : "Inter",
                color: theme.palette.gray[900],
                fontVariantNumeric: "tabular-nums",
                overflowWrap: "anywhere",
                whiteSpace: "normal",
            }}
        >
            {primary}
        </Typography>
        {secondary && (
            <Typography
                sx={{
                    fontSize: "12px",
                    color: theme.palette.gray[500],
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                }}
            >
                {secondary}
            </Typography>
        )}
    </Box>
);

const isTotal = (params: GridRenderCellParams) => params.id === TOTAL_ROW_ID;

const textColumn = (
    field: string,
    headerName: string,
    flex: number,
    minWidth: number,
    secondaryField?: string,
    format: (value: any) => string = (value) => value || "—",
): IColDef => ({
    field,
    headerName,
    flex,
    minWidth,
    sortable: false,
    renderCell: (params) => {
        if (isTotal(params) && !params.row[field]) return null;
        return (
            <TwoLineCell
                primary={format(params.row[field])}
                secondary={
                    secondaryField ? params.row[secondaryField] : undefined
                }
                bold={
                    field === "name" || field === "userName" || isTotal(params)
                }
            />
        );
    },
});

const numberColumn = (
    field: string,
    headerName: string,
    width: number,
    format: (value: number) => string = formatNumber,
    secondary?: (value: number) => string,
): IColDef => ({
    field,
    headerName,
    width,
    sortable: false,
    headerAlign: "right",
    renderCell: (params) => (
        <TwoLineCell
            primary={format(params.row[field] ?? 0)}
            secondary={secondary?.(params.row[field] ?? 0)}
            bold={isTotal(params)}
            alignRight
        />
    ),
});

const chipColumn = (
    field: string,
    headerName: string,
    width: number,
    chip: (row: any) => { label: string; color: "green" | "gray" | "blue" },
): IColDef => ({
    field,
    headerName,
    width,
    sortable: false,
    renderCell: (params) => {
        if (isTotal(params)) return null;
        const { label, color } = chip(params.row);
        return (
            <Box sx={{ px: 2 }}>
                <AcxChip size="small" label={label} color={color} />
            </Box>
        );
    },
});

const cadenceChip = (row: { cadence: string }) => ({
    label: row.cadence,
    color: "blue" as const,
});
const statusChip = (row: { isActive: boolean }) => ({
    label: row.isActive ? "Active" : "Inactive",
    color: row.isActive ? ("green" as const) : ("gray" as const),
});

export const chatColumns: IColDef[] = [
    textColumn("name", "User", 2, 220, "email"),
    textColumn("role", "Role", 1.2, 180),
    numberColumn("messages", "Messages", 110),
    numberColumn(
        "listeningTimeSavedMs",
        "Listening time saved ↓",
        180,
        formatHours,
        formatBusinessDays,
    ),
    numberColumn("montagesCreated", "Montages created", 140),
    textColumn("lastActive", "Last active", 0, 130, undefined, formatDate),
];

export const searchColumns: IColDef[] = [
    textColumn("name", "Subscription", 2, 240, "totalSub"),
    textColumn("ownerName", "Owner", 1.5, 220, "ownerEmail"),
    textColumn("ownerRole", "Role", 1.2, 180),
    chipColumn("cadence", "Cadence", 110, cadenceChip),
    chipColumn("kboAnalysisEnabled", "KBo analysis", 140, (row) => ({
        label: row.kboAnalysisEnabled ? "Included" : "Not included",
        color: row.kboAnalysisEnabled ? "green" : "gray",
    })),
    chipColumn("isActive", "Status", 110, statusChip),
    numberColumn("emailsSent", "Emails sent", 110),
    textColumn("lastRun", "Last sent", 0, 190, undefined, formatDateTime),
    textColumn("nextRun", "Next send", 0, 190, undefined, formatDateTime),
];

export const flowColumns: IColDef[] = [
    textColumn("userName", "User Name", 1.2, 180, "totalSub"),
    textColumn("userEmail", "User Email", 1.6, 240),
    textColumn("role", "Role", 1.2, 180),
    chipColumn("cadence", "Cadence", 110, cadenceChip),
    textColumn("lastRun", "Last Run", 0, 190, undefined, formatDateTime),
    textColumn("nextRun", "Next Run", 0, 190, undefined, formatDateTime),
    chipColumn("isActive", "Status", 110, statusChip),
    numberColumn("emailsSent", "Emails sent", 110),
];

/** A pinned totals row; its label lands in the first column's field. */
export const totalRow = (
    labelField: string,
    label: string,
    subField: string,
    sub: string,
    values: Record<string, number>,
) => ({
    id: TOTAL_ROW_ID,
    [labelField]: label,
    [subField]: sub,
    ...values,
});
